import { useMemo } from 'react'

import DOMPurify from 'dompurify'

const UNTITLED_IDEA_NAME = 'Untitled idea'
const IDEA_NAME_MAX_LENGTH = 48

type IdeaNameSource = {
  id: string
  content?: {
    title?: string | null
    text?: string | null
  } | null
}

const toPlainIdeaText = (text: string | undefined | null): string => {
  const trimmedText = (text ?? '').trim()
  if (!trimmedText) {
    return ''
  }

  const sanitizedNode = DOMPurify.sanitize(trimmedText, { RETURN_DOM: true })
  const textWalker = document.createTreeWalker(
    sanitizedNode,
    NodeFilter.SHOW_TEXT,
  )
  const textParts: string[] = []
  while (textWalker.nextNode()) {
    textParts.push(textWalker.currentNode.textContent ?? '')
  }

  return textParts.join(' ').replace(/\s+/g, ' ').trim()
}

const truncateIdeaName = (text: string): string => {
  if (text.length <= IDEA_NAME_MAX_LENGTH) {
    return text
  }

  const sliced = text.slice(0, IDEA_NAME_MAX_LENGTH)
  const lastSpace = sliced.lastIndexOf(' ')
  const clipped =
    lastSpace > IDEA_NAME_MAX_LENGTH / 2 ? sliced.slice(0, lastSpace) : sliced
  return clipped.trimEnd()
}

const getIdeaAccessibleName = (idea: IdeaNameSource): string => {
  const title = idea.content?.title?.trim()
  if (title) {
    return title
  }

  const text = toPlainIdeaText(idea.content?.text)
  if (text) {
    return `${truncateIdeaName(text)} (${idea.id})`
  }

  return idea.id ? `${UNTITLED_IDEA_NAME} (${idea.id})` : UNTITLED_IDEA_NAME
}

const sanitizeAndHighlightLinks = (text: string | undefined | null): string => {
  const trimmedText = (text ?? '').trim()
  if (!trimmedText) {
    return ''
  }
  // sanitize and strip out tags
  const sanitizedText = DOMPurify.sanitize(trimmedText, { ALLOWED_TAGS: [] })
  return sanitizedText.replace(
    /[(http(s)?)://(www.)?a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/gi,
    '<b>$&</b>',
  )
}

const useSanitizedAndHighlightedText = (
  text: string | undefined | null,
): string => {
  return useMemo(() => sanitizeAndHighlightLinks(text), [text])
}

export {
  getIdeaAccessibleName,
  sanitizeAndHighlightLinks,
  useSanitizedAndHighlightedText,
}
